import * as React from "react";
import * as moment from "moment";
import { observer, inject } from "mobx-react";
import { UncontrolledTooltip } from "reactstrap";

import BootstrapTable from "react-bootstrap-table-next";

import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "src/stores/CustomerStore";
import { SessionStore } from "src/stores/SessionStore";

import { InsuranceRecord, InsuranceCompany } from "src/model/Customer";
import { AttachmentLog, AttachmentFile } from "src/model/Attachment";

import { InsuranceRecordRequest, AdHocInsuranceCompanyRequest } from "src/requests/CustomerRequests";
import { FileUploadRequest, FileUploadRequestFromAttachmentFile } from "src/requests/CommonRequest";
import { AddressRequest, EditableAddress } from "src/requests/CommonRequest";

import { InputGroup, TextareaGroup, SelectGroup } from "../common/SimpleFormComponents";
import { successMessage, errorMessage } from "../common/Toast";
import LoadingWidget from "../common/LoadingWidget";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";

import * as fileUtil from "../../common/Methods/FileUtil";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faDownload, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import AttachmentsForm from "../common/AttachmentsForm";

/*
Editable Field Definitions
*/

export namespace EditableInsuranceRecord {
    export interface Fields {
        policyInception: moment.Moment;
        brokerName: string;

        companyId: number | null;
        attachmentLog: Array<FileUploadRequest>;
    }

    export const Empty = (): Fields => {
        return {
            policyInception: moment(),
            brokerName: "",

            companyId: null,
            attachmentLog: []
        };
    };

    export const FromModel = (m: InsuranceRecord): Fields => {
        return {
            policyInception: moment(m.policyInception),
            brokerName: m.brokerName,
            companyId: m.company.type === "EXISTING" ? m.company.id : null,
            attachmentLog: m.attachmentLog.files.map(f => FileUploadRequestFromAttachmentFile(f))
        };
    };

    export const ToRequest = (fields: Fields, adHocInfo: AdHocInsuranceCompanyRequest | undefined): InsuranceRecordRequest => {
        return {
            policyInception: fields.policyInception.valueOf(),
            brokerName: fields.brokerName,

            companyId: fields.companyId || undefined,
            adHocInfo,

            attachments: fields.attachmentLog
        };
    };
}

export namespace EditableAdHocInsuranceCompany {
    export interface Fields {
        businessName: string;
        contactName: string;
        phone: string;
        email: string;
    }

    export const Empty = (): Fields => {
        return {
            businessName: "",
            contactName: "",
            phone: "",
            email: ""
        };
    };

    export const FromModel = (m: InsuranceCompany): Fields => {
        return {
            businessName: m.businessName,
            contactName: m.contactName,
            phone: m.phone || "",
            email: m.email || ""
        };
    };

    export const ToRequest = (fields: Fields, address: AddressRequest | undefined): AdHocInsuranceCompanyRequest => {
        return {
            businessName: fields.businessName,
            contactName: fields.contactName,
            address,
            phone: fields.phone,
            email: fields.email
        };
    };
}

export const EditableAddressFromModel = (m: InsuranceCompany): EditableAddress.Fields => {
    if (!m.address) {
        return EditableAddress.Empty();
    }
    return {
        address: m.address.address || "",
        city: m.address.city || "",
        provinceId: m.address.province ? m.address.province.id : null,
        countryId: m.address.country ? m.address.country.id : null,
        postalCode: m.address.postalCode || ""
    };
};

/*
Components
*/

export interface Props {
    configurationStore?: ConfigurationStore;
    customerStore?: CustomerStore;
    attachmentLogEndpoints?: AttachmentLogEndpoints;
    customerEndpoints?: CustomerEndpoints;
    sessionStore?: SessionStore;
}

export interface PageState {
    editingRecordId: number | null;
    wasValidated: boolean;
    showForm: boolean;
}

type State = PageState & EditableInsuranceRecord.Fields & EditableAdHocInsuranceCompany.Fields & EditableAddress.Fields;

const DefaultState = (): State => {
    return {
        ...EditableInsuranceRecord.Empty(),
        ...EditableAdHocInsuranceCompany.Empty(),
        ...EditableAddress.Empty(),
        editingRecordId: null,
        wasValidated: false,
        showForm: false
    };
};

@inject("sessionStore")
@inject("configurationStore")
@inject("attachmentLogEndpoints")
@inject("customerStore")
@inject("customerEndpoints")
@observer
export default class Insurance extends React.Component<Props, State> {
    state: State = DefaultState();
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
    }

    handleOnChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>): void => {
        event.preventDefault();
        this.setState({ [event.target.name]: event.target.value } as any);
    };

    onSelectInsuranceCompany = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const companyId = e.target.value === "" ? null : parseInt(e.target.value, 10);
        this.setState({ companyId });
        if (companyId === null) {
            this.setState({
                ...EditableAdHocInsuranceCompany.Empty(),
                ...EditableAddress.Empty()
            });
        } else {
            this.onSelectExistingCompany(companyId);
        }
    };

    onSelectExistingCompany = (companyId: number): void => {
        const configurationStore = this.props.configurationStore!;

        const insuranceCompany = configurationStore.insuranceCompanies.find(insurance => insurance.id === companyId);
        if (!insuranceCompany) {
            throw new Error("Could not find insurance company in onSelectExistingCompany: " + companyId);
        }

        this.setState({
            ...EditableAdHocInsuranceCompany.FromModel(insuranceCompany),
            ...EditableAddressFromModel(insuranceCompany)
        });
    };

    editFields = (row: InsuranceRecord, insuranceCompanyId: number) => {
        this.setState({
            editingRecordId: row.id,
            ...EditableInsuranceRecord.FromModel(row),
            ...EditableAdHocInsuranceCompany.FromModel(row.company),
            ...EditableAddressFromModel(row.company),
            showForm: true
        });
    };

    generateRequest = (): InsuranceRecordRequest => {
        const address = this.state.companyId === null ? EditableAddress.ToRequest(this.state) : undefined;
        const adHocRequest = this.state.companyId === null ? EditableAdHocInsuranceCompany.ToRequest(this.state, address) : undefined;
        const request = EditableInsuranceRecord.ToRequest(this.state, adHocRequest);

        return request;
    };

    createInsuranceRecord = async () => {
        try {
            const request = this.generateRequest();
            await this.props.customerStore!.createInsuranceRecord(request);
            this.clearForm();
            successMessage(`A new insurance record has been created.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    editInsuranceRecord = async () => {
        try {
            const request = this.generateRequest();
            if (this.state.editingRecordId) {
                await this.props.customerStore!.editInsuranceRecord(this.state.editingRecordId, request);
                this.clearForm();
                successMessage(`Changes successfully saved.`);
            }
        } catch (error) {
            errorMessage(error);
        }
    };

    cancelInsuranceRecord = (insuranceRecord: InsuranceRecord) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        event.stopPropagation();

        const goAhead = await SimpleConfirmation(`Are you sure you'd like to cancel this insurance record?`);
        if (!goAhead) {
            return;
        }
        try {
            await this.props.customerStore!.cancelInsuranceRecord(insuranceRecord.id);
            this.clearForm();
            successMessage(`This insurance record has been cancelled.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    getAttachment = (file: AttachmentFile) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        try {
            const blob = await this.props.attachmentLogEndpoints!.getAttachment(file.id);
            fileUtil.DownloadBlob(file.name, blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    submit = (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        event.stopPropagation();

        if (
            this.form &&
            this.form.checkValidity() &&
            (this.state.companyId !== null || this.state.companyId !== "") &&
            this.state.businessName !== ""
        ) {
            this.state.editingRecordId ? this.editInsuranceRecord() : this.createInsuranceRecord();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    clearForm = () => {
        this.setState(DefaultState());
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }

        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            throw new Error(" ** No Customer **");
        }

        const insuranceCompanies = configurationStore.insuranceCompanies;

        const disableProvince: boolean =
            this.state.companyId === null || this.state.companyId === null || this.state.companyId === null
                ? this.state.companyId === null && this.state.countryId !== null
                    ? false
                    : true
                : this.state.countryId !== null
                ? true
                : false;

        const disableInsuranceCompanyFields = this.state.companyId !== null;
        const insuranceRecords = customer.insuranceRecords.slice().sort((a: InsuranceRecord, b: InsuranceRecord) => b.dateCreated - a.dateCreated);

        const columns = [
            {
                dataField: "businessName",
                text: "Insurance Company",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.company.businessName;
                }
            },
            {
                dataField: "businessName2",
                text: "Business Name",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.company.businessName;
                }
            },
            {
                dataField: "contactName",
                text: "Contact Name",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.company.contactName;
                }
            },
            {
                dataField: "address",
                text: "Address",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    let address = " - ";
                    if (row.company.address) {
                        address =
                            (row.company.address.address ? row.company.address.address + ", " : "") +
                            (row.company.address.city ? row.company.address.city + ", " : "") +
                            (row.company.address.province ? row.company.address.province.name + " " : "") +
                            (row.company.address.postalCode ? " " + row.company.address.postalCode : "") +
                            (row.company.address.country ? " " + row.company.address.country.name : "");
                    }
                    return address;
                }
            },
            {
                dataField: "phone",
                text: "Phone",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.company.phone ? row.company.phone : " - ";
                }
            },
            {
                dataField: "email",
                text: "Email",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.company.email ? row.company.email : " - ";
                }
            },
            {
                dataField: "policyInception",
                text: "Date Created",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.policyInception ? moment(row.policyInception).format("YYYY-MM-DD") : " - ";
                }
            },
            {
                dataField: "dateCancelled",
                text: "Date Cancelled",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.dateCancelled ? moment(row.dateCancelled).format("YYYY-MM-DD") : " - ";
                }
            },
            {
                dataField: "state",
                text: "Status",
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    return row.dateCancelled ? "Cancelled" : "Active";
                }
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "100px", textAlign: "center" },
                formatter: (cellContent: any, row: InsuranceRecord) => {
                    const insuranceCompanyRelated = this.props.configurationStore!.insuranceCompanies.filter(
                        company => company.id === row.company.id
                    );
                    const insuranceCompanyId = insuranceCompanyRelated.length > 0 ? insuranceCompanyRelated[0].id : 0;

                    if (row.dateCancelled !== null) {
                        return <div />;
                    }

                    return (
                        <>
                            <div className="btn-group btn-group-toggle mt-1 mr-1" data-toggle="buttons">
                                {row.attachmentLog.files.length > 0 && (
                                    <>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={this.getAttachment(row.attachmentLog.files[0])}
                                            id={`tooltipDownload${row.id}`}
                                        >
                                            <FontAwesomeIcon icon={faDownload} />
                                        </button>
                                        <UncontrolledTooltip placement="top" target={`tooltipDownload${row.id}`} delay={0} className="tooltipstrap">
                                            Download File - {row.attachmentLog.files[0].name}
                                        </UncontrolledTooltip>
                                    </>
                                )}
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.editFields(row, insuranceCompanyId);
                                    }}
                                    id={`tooltipEdit${row.id}`}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>

                                <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                    Edit Insurance Record
                                </UncontrolledTooltip>
                            </div>
                            <button
                                className="btn btn-info btn-sm mt-1"
                                style={{ padding: ".3rem .65rem" }}
                                onClick={this.cancelInsuranceRecord(row)}
                            >
                                Cancel
                            </button>
                        </>
                    );
                }
            }
        ];

        return (
            <>
                <div className="card shadow">
                    <div className="card-header">Insurance</div>
                    <div className="card-body">
                        {!this.state.showForm ? (
                            <div className="row mb-4 mx-0">
                                <div className="col-12 mx-0 px-0 text-right">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            this.setState({ showForm: true });
                                        }}
                                    >
                                        {" "}
                                        New Insurance Record
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="row mb-4  mx-0">
                                <div className="col-12  mx-0 px-0">
                                    <div className="card mx-0" style={{ backgroundColor: "#f7faff" }}>
                                        <div className="card-body pt-0">
                                            <form
                                                id="insurance-form"
                                                ref={form => (this.form = form)}
                                                className={"needs-validation mt-3" + (this.state.wasValidated ? " was-validated" : "")}
                                                onSubmit={e => this.submit(e)}
                                                noValidate={true}
                                            >
                                                <div className="form-row mb-4">
                                                    <SelectGroup
                                                        label="Insurance Company:"
                                                        name="documentType"
                                                        isRequired={true}
                                                        className="col-md-12 col-lg-8"
                                                        disabled={this.state.editingRecordId ? true : false}
                                                        isInvalid={
                                                            this.state.wasValidated &&
                                                            (this.state.companyId === null || this.state.companyId === null)
                                                        }
                                                        value={this.state.companyId || ""}
                                                        onChange={e => this.onSelectInsuranceCompany(e)}
                                                    >
                                                        {insuranceCompanies
                                                            .slice()
                                                            .sort((a: InsuranceCompany, b: InsuranceCompany) =>
                                                                a.businessName.toLowerCase().localeCompare(b.businessName.toLowerCase())
                                                            )
                                                            .map((c: InsuranceCompany, i: number) => (
                                                                <option key={c.id} value={c.id}>
                                                                    {c.businessName}
                                                                </option>
                                                            ))}
                                                        <option value="">Other</option>
                                                    </SelectGroup>
                                                </div>
                                                <div className="form-row">
                                                    <InputGroup
                                                        label="Business Name:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="businessName"
                                                        isRequired={true}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.businessName || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                    <InputGroup
                                                        label="Address:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="address"
                                                        isRequired={false}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.address || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                    <InputGroup
                                                        label="City:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="city"
                                                        isRequired={false}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.city || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                </div>
                                                <div className="form-row mb-4">
                                                    <InputGroup
                                                        label="Postal Code:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="postalCode"
                                                        isRequired={false}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.postalCode || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                    <SelectGroup
                                                        label="Country:"
                                                        name="countryId"
                                                        isRequired={false}
                                                        className="col-md-12 col-lg-4"
                                                        disabled={disableInsuranceCompanyFields}
                                                        isInvalid={false}
                                                        value={this.state.countryId || ""}
                                                        onChange={e => this.setState({ countryId: parseInt(e.target.value, 10) })}
                                                    >
                                                        <option value=""> Select a country...</option>
                                                        {configurationStore.countries.map(country => (
                                                            <option key={country.id} value={country.id}>
                                                                {country.name} ({country.abbreviation})
                                                            </option>
                                                        ))}
                                                    </SelectGroup>
                                                    <SelectGroup
                                                        label="Province:"
                                                        name="provinceId"
                                                        isRequired={false}
                                                        className="col-md-12 col-lg-4"
                                                        disabled={disableProvince}
                                                        isInvalid={false}
                                                        value={this.state.provinceId || ""}
                                                        onChange={e => this.setState({ provinceId: parseInt(e.target.value, 10) })}
                                                    >
                                                        <option value=""> Select a province...</option>
                                                        {configurationStore.provinces
                                                            .filter(province => province.countryId === this.state.countryId)
                                                            .map(province => (
                                                                <option key={province.id} value={province.id}>
                                                                    {province.name} ({province.abbreviation})
                                                                </option>
                                                            ))}
                                                    </SelectGroup>
                                                </div>
                                                <div className="form-row mb-4">
                                                    <InputGroup
                                                        label="Contact Name:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="contactName"
                                                        isRequired={false}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.contactName || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                    <InputGroup
                                                        label="Phone:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="phone"
                                                        isRequired={false}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.phone || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                    <InputGroup
                                                        label="Email:"
                                                        className="col-md-12 col-lg-4"
                                                        type="string"
                                                        name="email"
                                                        isRequired={false}
                                                        disabled={disableInsuranceCompanyFields}
                                                        value={this.state.email || ""}
                                                        onChange={this.handleOnChange}
                                                    />
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group col-md-12">
                                                        <label htmlFor="files">Attach File:</label>
                                                        <AttachmentsForm
                                                            attachments={this.state.attachmentLog}
                                                            updateAttachmentLog={attachmentLog => this.setState({ attachmentLog })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="for-row text-right">
                                                    <div className="col-12 pr-0">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                this.clearForm();
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button type="submit" form="insurance-form" className="btn btn-primary btn-sm mr-0 ml-3">
                                                            {this.state.editingRecordId ? "Edit" : "Create"} Insurance Record
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="table-responsive">
                            <BootstrapTable
                                keyField="id"
                                remote={true}
                                hover={true}
                                bordered={false}
                                className="table-sm "
                                columns={columns}
                                bootstrap4={true}
                                data={insuranceRecords}
                                noDataIndication={() => <div>{customerStore.loading ? <LoadingWidget /> : <span>No results found.</span>}</div>}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/Insurance.tsx